import { BadgeBlock } from "./blocks/BadgeBlock";
import { CalloutBlock } from "./blocks/CalloutBlock";
import { CardBlock } from "./blocks/CardBlock";
import { ExpandableBlock } from "./blocks/ExpandableBlock";
import { FilePreviewBlock } from "./blocks/FilePreviewBlock";
import { GridBlock } from "./blocks/GridBlock";
import { HeadingBlock } from "./blocks/HeadingBlock";
import { ListBlock } from "./blocks/ListBlock";
import { MetricBlock } from "./blocks/MetricBlock";
import { ParagraphBlock } from "./blocks/ParagraphBlock";
import { SectionBlock } from "./blocks/SectionBlock";
import { SourceLinkBlock } from "./blocks/SourceLinkBlock";
import { TableBlock } from "./blocks/TableBlock";
import { type CanvasBlock } from "./types";

interface CanvasBlockRendererProps {
  block: CanvasBlock;
}

export function CanvasBlockRenderer({ block }: CanvasBlockRendererProps) {
  switch (block.type) {
    case "heading":
      return <HeadingBlock level={block.level} text={block.text} />;

    case "paragraph":
      return <ParagraphBlock text={block.text} />;

    case "badge":
      return (
        <BadgeBlock label={block.label} color={block.color} size={block.size} />
      );

    case "card":
      return (
        <CardBlock
          title={block.title}
          subtitle={block.subtitle}
          badge={block.badge}
        >
          {block.children}
        </CardBlock>
      );

    case "grid":
      return <GridBlock columns={block.columns}>{block.children}</GridBlock>;

    case "table":
      return (
        <TableBlock
          headers={block.headers}
          rows={block.rows}
          highlightRules={block.highlightRules}
        />
      );

    case "list":
      return <ListBlock ordered={block.ordered} items={block.items} />;

    case "divider":
      return <hr className="border-puntt-neutral-gray-5" />;

    case "metric":
      return (
        <MetricBlock
          label={block.label}
          value={block.value}
          trend={block.trend}
        />
      );

    case "expandable":
      return (
        <ExpandableBlock title={block.title} summary={block.summary}>
          {block.children}
        </ExpandableBlock>
      );

    case "section":
      return (
        <SectionBlock
          id={block.id}
          title={block.title}
          editable={block.editable}
        >
          {block.children}
        </SectionBlock>
      );

    case "source_link":
      return (
        <SourceLinkBlock
          title={block.title}
          url={block.url}
          linkType={block.linkType}
        />
      );

    case "chart":
      // Placeholder for chart rendering (future: integrate a charting library)
      return (
        <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 text-sm text-puntt-neutral-gray-9">
          Chart: {block.chartType}
        </div>
      );

    case "callout":
      return <CalloutBlock variant={block.variant} text={block.text} />;

    case "file_preview":
      return (
        <FilePreviewBlock
          url={block.url}
          fileName={block.fileName}
          fileType={block.fileType}
          pageCount={block.pageCount}
        />
      );

    default:
      return null;
  }
}
